import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CalendarClock, ClipboardCheck, RefreshCw, ShieldAlert } from 'lucide-react';
import { attendanceUiText } from '@/lib/nfcUiVisibility';
import { apiFetch } from '../lib/supabaseClient';
import '../styles/audit.css';

const unavailable = 'Dato non disponibile';
const getRows = (value) => (Array.isArray(value) ? value : (value?.items || value?.data || value?.records || []));
const first = (...values) => values.find((value) => value !== undefined && value !== null && value !== '') || null;
const identify = (label, name, id) => {
  const value = first(name, id);
  return value ? `${label}: ${value}` : null;
};

function actionLabel(record) {
  const action = String(first(record?.action, record?.event, '')).toLowerCase();
  const labels = {
    'account.archive': 'Account archiviato',
    'account.create': 'Account creato',
    'account.email_changed': 'Email account modificata',
    'account.invite_resent': 'Invito account reinviato',
    'account.reactivate': 'Account riattivato',
    'account.recovery_sent': 'Recupero account inviato',
    'account.restore': 'Account ripristinato',
    'account.sessions_revoked': 'Sessioni account revocate',
    'account.suspend': 'Account sospeso',
    'assignment.cancelled': 'Assegnazione annullata',
    'assignment.closed': 'Assegnazione chiusa',
    'assignment.created': 'Assegnazione creata',
    'assignment.temporary_added': 'Assegnazione temporanea aggiunta',
    'assignment.transferred': 'Assegnazione trasferita',
    'assignment.updated': 'Assegnazione aggiornata',
    'availability.reset': 'Disponibilità ripristinate',
    'availability.updated': 'Disponibilità aggiornate',
    'company.create': 'Azienda creata',
    'company.delete': 'Azienda eliminata',
    'contract.closed': 'Contratto chiuso',
    'contract.created': 'Contratto creato',
    'contract.updated': 'Contratto aggiornato',
    'coverage_rule.created': 'Regola di copertura creata',
    'coverage_rule.deleted': 'Regola di copertura eliminata',
    'coverage_rule.updated': 'Regola di copertura aggiornata',
    'department.created': 'Reparto creato',
    'department.deleted': 'Reparto eliminato',
    'department.updated': 'Reparto aggiornato',
    'document.archived': 'Documento archiviato',
    'document.uploaded': 'Documento caricato',
    'employee.create': 'Persona aggiunta',
    'employee.created': 'Persona aggiunta',
    'employee.identity_edited': 'Identità persona aggiornata',
    'employee.updated': 'Dati persona aggiornati',
    'employee.view': 'Scheda personale consultata',
    'ferie.approved': 'Ferie approvate',
    'ferie.fabbisogno_gruppo.created': 'Gruppo fabbisogno creato',
    'ferie.fabbisogno_gruppo.deleted': 'Gruppo fabbisogno rimosso',
    'ferie.fabbisogno_gruppo.updated': 'Gruppo fabbisogno aggiornato',
    'leave.approved': 'Ferie approvate',
    'location.create': 'Locale creato',
    'location.delete': 'Locale eliminato',
    'period.archived': 'Periodo archiviato',
    'period.copied_previous': 'Periodo precedente copiato',
    'period.created': 'Periodo creato',
    'period.reopened': 'Periodo riaperto',
    'period.undo': 'Modifica periodo annullata',
    'period.updated': 'Periodo aggiornato',
    'security.user_updated': 'Sicurezza utente aggiornata',
    'shift.cancelled': 'Turno annullato',
    'shift.created': 'Turno creato',
    'shift.double_created': 'Doppio turno creato',
    'shift.published': 'Turno pubblicato',
    'shift.replacement_confirmed': 'Sostituzione confermata',
    'shift.republished': 'Turno ripubblicato',
    'shift.updated': 'Turno aggiornato',
    'shift.warning_confirmed': 'Avviso turno confermato',
    'user.create': 'Utente creato',
  };
  if (labels[action]) return labels[action];
  return first(record?.action_label, record?.description) || 'Attività registrata';
}

function dateTime(value) {
  if (!value || Number.isNaN(new Date(value).getTime())) return unavailable;
  return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

function Audit() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const load = () => {
    setLoading(true);
    setError('');
    apiFetch('/api/audit')
      .then((data) => setRecords(getRows(data)))
      .catch((requestError) => setError(requestError.message || 'Registro non disponibile'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <main className="audit-shell" data-testid="audit-page">
      <header className="audit-header">
        <Link to="/foundation" className="audit-brand" aria-label="Torna alla dashboard">
          <img src="/brand/restaurantos-logo-horizontal.svg" alt="RestaurantOS" />
        </Link>
        <Link to="/foundation" className="audit-back"><ArrowLeft size={15} /> Dashboard</Link>
      </header>
      <section className="audit-main">
        <div className="audit-title-row">
          <div><span className="audit-kicker">SICUREZZA E CONTROLLO</span><h1>Tracciabilità</h1><p>Registro delle attività disponibili nel tuo perimetro autorizzato.</p></div>
          <button type="button" className="audit-refresh" onClick={load} disabled={loading}><RefreshCw size={15} className={loading ? 'audit-spin' : ''} /> Aggiorna</button>
        </div>
        {error && <div className="audit-error" role="alert"><ShieldAlert size={17} /> <span>{error}</span><button type="button" onClick={load}>Riprova</button></div>}
        <section className="audit-card" aria-label="Registro attività">
          <div className="audit-card-head"><div><span>REGISTRO ATTIVITÀ</span><strong>{loading ? 'Caricamento' : `${records.length} ${records.length === 1 ? 'evento' : 'eventi'}`}</strong></div><ClipboardCheck size={18} /></div>
          {loading ? <div className="audit-skeleton"><i /><i /><i /><i /></div> : records.length ? (
            <div className="audit-table" role="table">
              <div className="audit-columns" role="row"><span>Azione</span><span>Autore</span><span>Perimetro</span><span>Data e ora</span></div>
              {records.map((record, index) => {
                const company = identify('Azienda', first(record.company_name, record.company?.name), record.company_id);
                const location = identify('Locale', first(record.location_name, record.location?.name), record.location_id);
                const department = identify('Reparto', first(record.department_name, record.department?.name), record.department_id);
                const scope = [company, location, department].filter(Boolean).join(' · ') || unavailable;
                return <div className="audit-record" role="row" key={record.id || `${record.created_at || record.timestamp}-${index}`}>
                  <span><b>{attendanceUiText(actionLabel(record))}</b><small>{attendanceUiText(first(record.entity_type, record.resource_type)) || unavailable}</small></span>
                  <span>{first(record.actor_name, record.user_name, record.actor?.name, record.user_id) || unavailable}</span>
                  <span>{scope}</span>
                  <time dateTime={record.created_at || record.timestamp}>{dateTime(record.created_at || record.timestamp)}</time>
                </div>;
              })}
            </div>
          ) : <div className="audit-empty"><CalendarClock size={22} /><strong>Nessuna attività registrata</strong><p>Quando saranno disponibili eventi nel perimetro selezionato, compariranno qui.</p></div>}
        </section>
      </section>
    </main>
  );
}

export default Audit;